// Mental Health Debate Topics and Conversations
export const DEBATE_TOPICS = {
  ANXIETY_TREATMENT: {
    id: 'anxiety_treatment',
    title: 'Best Approaches for Treating Anxiety',
    description: 'Comparing different therapeutic interventions for anxiety disorders',
    duration: 420, // 7 minutes in seconds
    messages: [
      {
        id: 1,
        speaker: 'dr_sarah',
        timestamp: 0,
        content: "Good afternoon, everyone. I'd like to start by highlighting the overwhelming evidence for Cognitive Behavioral Therapy in treating anxiety disorders. The 2024 meta-analysis by Johnson et al. shows 68% improvement rates in controlled studies.",
        typingDelay: 3000
      },
      {
        id: 2,
        speaker: 'dr_james',
        timestamp: 8000,
        content: "Dr. Sarah, while I respect the research, we must consider the whole person. Anxiety often stems from disconnection - from our bodies, our breath, our present moment awareness. CBT addresses thoughts, but what about the somatic experience?",
        typingDelay: 3500
      },
      {
        id: 3,
        speaker: 'dr_maria',
        timestamp: 16000,
        content: "Both perspectives have merit, but I'm concerned we're treating symptoms rather than root causes. Many of my clients with anxiety trace their patterns back to childhood attachment issues. How do we address the developmental foundations?",
        typingDelay: 4000
      },
      {
        id: 4,
        speaker: 'dr_sarah',
        timestamp: 24000,
        content: "Dr. Maria, I appreciate your depth perspective, but we need practical solutions that work within limited session time. CBT provides immediate tools - breathing techniques, thought challenging, exposure therapy. These are evidence-based and accessible.",
        typingDelay: 3200
      },
      {
        id: 5,
        speaker: 'dr_james',
        timestamp: 32000,
        content: "I understand the time constraints, Dr. Sarah, but quick fixes often lead to relapse. When we teach mindfulness and body awareness, clients develop lifelong skills. They learn to recognize anxiety's early signals and respond with compassion rather than resistance.",
        typingDelay: 3800
      },
      {
        id: 6,
        speaker: 'dr_maria',
        timestamp: 40000,
        content: "James, your approach resonates with me, but it requires clients to have a certain level of psychological insight. Many people need to understand WHY they're anxious before they can effectively manage it. That's where psychodynamic work becomes crucial.",
        typingDelay: 3600
      },
      {
        id: 7,
        speaker: 'dr_sarah',
        timestamp: 48000,
        content: "I hear both of you, but let's look at the data: CBT shows measurable results in 12-16 weeks. Insurance companies and clients need outcomes they can count on. We can't spend months exploring childhood when someone can't leave their house.",
        typingDelay: 3400
      },
      {
        id: 8,
        speaker: 'dr_james',
        timestamp: 56000,
        content: "Sarah, I'm not suggesting we abandon structure. Mindfulness-based CBT combines both approaches. We can teach practical skills while building awareness. The key is integration, not either/or thinking.",
        typingDelay: 3000
      },
      {
        id: 9,
        speaker: 'dr_maria',
        timestamp: 64000,
        content: "That's exactly my point - we need personalized approaches. Some clients respond beautifully to CBT, others need to process trauma first, and some benefit from the mindfulness integration James mentioned. The art is in assessment and matching.",
        typingDelay: 3700
      },
      {
        id: 10,
        speaker: 'dr_sarah',
        timestamp: 72000,
        content: "Agreed on personalization, Maria. But we need standardized assessment tools and clear treatment protocols. How do we ensure quality care while maintaining flexibility? Evidence-based practice doesn't mean one-size-fits-all.",
        typingDelay: 3500
      }
    ]
  },
  DIGITAL_VS_TRADITIONAL: {
    id: 'digital_vs_traditional',
    title: 'Digital Therapy vs Traditional Sessions',
    description: 'The role of technology in mental health care delivery',
    duration: 360, // 6 minutes
    messages: [
      {
        id: 1,
        speaker: 'dr_sarah',
        timestamp: 0,
        content: "The digital therapy revolution is here, and the data is compelling. Studies show teletherapy is as effective as in-person sessions for most conditions, with 85% client satisfaction rates. We're reaching people who never would have sought help.",
        typingDelay: 3200
      },
      {
        id: 2,
        speaker: 'dr_maria',
        timestamp: 8000,
        content: "Sarah, I'm concerned about what we lose in digital spaces. Therapy requires presence, attunement, and the subtle nonverbal cues that build deep connection. Can we truly establish the therapeutic alliance through a screen?",
        typingDelay: 3800
      },
      {
        id: 3,
        speaker: 'dr_james',
        timestamp: 16000,
        content: "Maria raises an important point about presence, but technology can actually enhance mindfulness practices. Guided meditation apps, biofeedback devices, and virtual reality exposure therapy offer new dimensions of healing we couldn't access before.",
        typingDelay: 3600
      },
      {
        id: 4,
        speaker: 'dr_sarah',
        timestamp: 24000,
        content: "James, those innovations are exciting, but let's address accessibility. Digital platforms eliminate transportation barriers, childcare issues, and geographic limitations. We're democratizing mental health care in unprecedented ways.",
        typingDelay: 3000
      },
      {
        id: 5,
        speaker: 'dr_maria',
        timestamp: 32000,
        content: "Accessibility is crucial, but I worry about the commodification of therapy. Apps and AI chatbots can't replace human empathy and intuition. We risk reducing therapy to a transaction rather than a transformative relationship.",
        typingDelay: 3700
      },
      {
        id: 6,
        speaker: 'dr_james',
        timestamp: 40000,
        content: "I understand your concerns, Maria, but technology can actually deepen the therapeutic relationship. Clients often feel safer sharing in their own space. And we can use digital tools to extend care between sessions - mood tracking, journaling prompts.",
        typingDelay: 3500
      },
      {
        id: 7,
        speaker: 'dr_sarah',
        timestamp: 48000,
        content: "The hybrid model is the future. In-person for initial assessments and crisis intervention, digital for maintenance and skill-building. We can serve more people effectively while maintaining quality standards.",
        typingDelay: 2800
      },
      {
        id: 8,
        speaker: 'dr_maria',
        timestamp: 56000,
        content: "I can see the benefits of hybrid approaches, but we need to ensure we're not creating a two-tier system. Will digital therapy become the 'budget option' while in-person therapy becomes a luxury?",
        typingDelay: 3400
      }
    ]
  },
  WORK_LIFE_BALANCE: {
    id: 'work_life_balance',
    title: 'Work-Life Balance in Modern Times',
    description: 'Strategies for preventing burnout and maintaining mental wellness',
    duration: 480, // 8 minutes
    messages: [
      {
        id: 1,
        speaker: 'dr_maria',
        timestamp: 0,
        content: "The modern workplace is creating unprecedented levels of stress and burnout. I see clients who are constantly 'on' - checking emails at midnight, feeling guilty for taking vacation. We need to examine the deeper psychological drivers of this behavior.",
        typingDelay: 4000
      },
      {
        id: 2,
        speaker: 'dr_sarah',
        timestamp: 10000,
        content: "Maria, you're right about the scale of the problem. The research shows 76% of workers experience burnout symptoms. But we need practical, implementable solutions. Boundary-setting techniques, time-blocking strategies, and stress management protocols can be taught systematically.",
        typingDelay: 3800
      },
      {
        id: 3,
        speaker: 'dr_james',
        timestamp: 20000,
        content: "Both of you are addressing important aspects, but I believe the root issue is our disconnection from natural rhythms. We're fighting against our biology - working against circadian cycles, ignoring our bodies' need for rest and restoration.",
        typingDelay: 3500
      },
      {
        id: 4,
        speaker: 'dr_maria',
        timestamp: 30000,
        content: "James, that's insightful, but we also need to consider the psychological factors. Many of my clients tie their self-worth to productivity. There's often a deep fear of being 'replaceable' or 'not enough' driving this overwork.",
        typingDelay: 3700
      },
      {
        id: 5,
        speaker: 'dr_sarah',
        timestamp: 40000,
        content: "That's a crucial insight, Maria. We can teach practical skills, but if someone's core belief is 'I'm only valuable when I'm producing,' they'll find ways around any boundary we set. We need cognitive restructuring alongside behavioral changes.",
        typingDelay: 3400
      },
      {
        id: 6,
        speaker: 'dr_james',
        timestamp: 50000,
        content: "Exactly, Sarah. And that's where mindfulness becomes powerful. When clients learn to observe their thoughts without judgment, they can recognize these patterns and choose differently. It's not about working harder at balance - it's about being present to what's actually needed.",
        typingDelay: 3800
      },
      {
        id: 7,
        speaker: 'dr_maria',
        timestamp: 60000,
        content: "I love that perspective, James. But we also need to address systemic issues. Many workplaces reward overwork and punish boundaries. How do we help clients navigate toxic work cultures while protecting their mental health?",
        typingDelay: 3600
      },
      {
        id: 8,
        speaker: 'dr_sarah',
        timestamp: 70000,
        content: "That's where assertiveness training and communication skills become crucial. We can teach clients to set boundaries professionally, negotiate workload, and advocate for reasonable expectations. It's about empowerment through skills.",
        typingDelay: 3200
      },
      {
        id: 9,
        speaker: 'dr_james',
        timestamp: 80000,
        content: "Skills are important, Sarah, but I also want to help clients reconnect with their values and purpose. When work aligns with personal meaning, it can be energizing rather than draining. The question becomes: 'Is this work serving my highest good?'",
        typingDelay: 3600
      },
      {
        id: 10,
        speaker: 'dr_maria',
        timestamp: 90000,
        content: "Beautiful integration, James. And Sarah, your practical approach gives clients concrete tools. The key is helping them understand their patterns, develop new skills, and make conscious choices about how they want to live and work.",
        typingDelay: 3400
      }
    ]
  },
  DEPRESSION_TREATMENT: {
    id: 'depression_treatment',
    title: 'Depression Treatment',
    description: 'Therapy vs medication approaches for treating depression',
    duration: 480, // 8 minutes
    messages: [
      {
        id: 1,
        speaker: 'dr_maria',
        timestamp: 0,
        content: "Depression is a complex condition that often requires a multifaceted approach. I've found that understanding the root causes - whether from childhood trauma, relationship patterns, or existential crises - is crucial before deciding on treatment.",
        typingDelay: 4000
      },
      {
        id: 2,
        speaker: 'dr_sarah',
        timestamp: 10000,
        content: "Maria, while I appreciate the depth of your approach, the research is clear: antidepressant medication combined with CBT shows 70% improvement rates in moderate to severe depression. We can't wait months for insight when someone is suicidal.",
        typingDelay: 3800
      },
      {
        id: 3,
        speaker: 'dr_james',
        timestamp: 20000,
        content: "Both of you make valid points, but I'm concerned we're treating depression as purely a brain chemistry issue or purely psychological. The body holds trauma, and somatic approaches, nutrition, and lifestyle changes can be transformative.",
        typingDelay: 3600
      },
      {
        id: 4,
        speaker: 'dr_sarah',
        timestamp: 30000,
        content: "James, lifestyle changes are important, but they're adjunctive treatments. When someone can't get out of bed, can't eat, and has no energy, medication can be life-saving. We need to meet people where they are.",
        typingDelay: 3400
      },
      {
        id: 5,
        speaker: 'dr_maria',
        timestamp: 40000,
        content: "Sarah, I'm not anti-medication, but we need to understand why someone is depressed. Are they depressed because of a chemical imbalance, or is the chemical imbalance a symptom of deeper psychological pain?",
        typingDelay: 3700
      },
      {
        id: 6,
        speaker: 'dr_james',
        timestamp: 50000,
        content: "That's exactly my point, Maria. Depression often signals that someone's life is out of alignment with their values, their body, their spirit. Medication can help stabilize, but we need to address the whole person.",
        typingDelay: 3500
      },
      {
        id: 7,
        speaker: 'dr_sarah',
        timestamp: 60000,
        content: "I agree we need a holistic approach, but let's be practical. Most people have limited time and resources. CBT provides concrete tools that work quickly and can be practiced independently. It's accessible and evidence-based.",
        typingDelay: 3600
      },
      {
        id: 8,
        speaker: 'dr_maria',
        timestamp: 70000,
        content: "CBT is valuable, Sarah, but some depressions are too deep for surface-level interventions. When someone has been depressed for years, they often need to process underlying trauma and develop new relationship patterns.",
        typingDelay: 3700
      }
    ]
  },
  CHILD_PSYCHOLOGY: {
    id: 'child_psychology',
    title: 'Child Psychology',
    description: 'Early intervention strategies for childhood mental health',
    duration: 360, // 6 minutes
    messages: [
      {
        id: 1,
        speaker: 'dr_maria',
        timestamp: 0,
        content: "Early intervention is absolutely critical. By the time children reach adolescence, patterns are deeply ingrained. I believe we need to work with the whole family system, not just the child, to create lasting change.",
        typingDelay: 3800
      },
      {
        id: 2,
        speaker: 'dr_sarah',
        timestamp: 10000,
        content: "I agree on early intervention, Maria, but we need evidence-based approaches. Play therapy and CBT for children show measurable results. We can't just rely on family dynamics - children need concrete coping skills.",
        typingDelay: 3500
      },
      {
        id: 3,
        speaker: 'dr_james',
        timestamp: 20000,
        content: "Both perspectives are important, but I think we're missing something fundamental. Children are incredibly resilient when they feel safe and connected. Mindfulness practices, nature exposure, and creative expression can be powerful healing tools.",
        typingDelay: 3600
      },
      {
        id: 4,
        speaker: 'dr_sarah',
        timestamp: 30000,
        content: "James, while those activities are beneficial, children with ADHD, anxiety, or behavioral issues need structured interventions. We can't just hope that nature and mindfulness will solve serious mental health challenges.",
        typingDelay: 3400
      },
      {
        id: 5,
        speaker: 'dr_maria',
        timestamp: 40000,
        content: "Sarah, you're right that we need structure, but children's symptoms often reflect family stress, trauma, or attachment issues. If we only treat the child's behavior without addressing the family context, we're missing the root cause.",
        typingDelay: 3700
      },
      {
        id: 6,
        speaker: 'dr_james',
        timestamp: 50000,
        content: "Maria, that's exactly what I mean about connection. When children feel truly seen and accepted - not just managed or fixed - they naturally begin to heal. The therapeutic relationship itself is often the most powerful intervention.",
        typingDelay: 3600
      }
    ]
  },
  SLEEP_DISORDERS: {
    id: 'sleep_disorders',
    title: 'Sleep Disorders',
    description: 'CBT-I vs sleep medication approaches',
    duration: 300, // 5 minutes
    messages: [
      {
        id: 1,
        speaker: 'dr_sarah',
        timestamp: 0,
        content: "Cognitive Behavioral Therapy for Insomnia (CBT-I) is the gold standard for treating sleep disorders. Studies show 70-80% improvement rates without the side effects or dependency risks of sleep medications.",
        typingDelay: 3500
      },
      {
        id: 2,
        speaker: 'dr_james',
        timestamp: 8000,
        content: "CBT-I is effective, Sarah, but I believe we need to address the underlying stress and anxiety that often drives insomnia. Meditation, breathwork, and creating a truly restful sleep environment are equally important.",
        typingDelay: 3600
      },
      {
        id: 3,
        speaker: 'dr_maria',
        timestamp: 16000,
        content: "Both of you are right, but we also need to explore what's keeping people awake at night. Often, insomnia is a symptom of unresolved emotional issues, trauma, or life transitions that need processing.",
        typingDelay: 3700
      },
      {
        id: 4,
        speaker: 'dr_sarah',
        timestamp: 24000,
        content: "Maria, while psychological factors matter, many people have developed maladaptive sleep habits that need behavioral correction. Sleep restriction, stimulus control, and sleep hygiene protocols work regardless of the underlying cause.",
        typingDelay: 3600
      },
      {
        id: 5,
        speaker: 'dr_james',
        timestamp: 32000,
        content: "Sarah, I'm not suggesting we ignore behavioral approaches, but when someone is chronically sleep-deprived, they're in survival mode. We need to address the nervous system regulation first - that's where mindfulness becomes crucial.",
        typingDelay: 3500
      },
      {
        id: 6,
        speaker: 'dr_maria',
        timestamp: 40000,
        content: "James, that's insightful about the nervous system. And Sarah, you're right about behavioral protocols. The key is understanding each person's unique relationship with sleep and creating a personalized approach that addresses both mind and body.",
        typingDelay: 3700
      }
    ]
  }
};

export const getTopicById = (topicId) => {
  return Object.values(DEBATE_TOPICS).find(topic => topic.id === topicId);
};

export const getAllTopics = () => {
  return Object.values(DEBATE_TOPICS);
};
